import { Button, Col, Modal, Row } from "react-bootstrap";
import { Navigate, useNavigate } from "react-router-dom";
import { useAppSelector } from "../../../store/hooks";
import { selectedOrganization } from "../../../store/organizationsSlice";

// Step 0 of route creation, IFR-enabled orgs only: VFR routes are drawn on the map, IFR routes are
// Field-15 airway templates validated with EUROCONTROL. Without the org opt-in there is no choice
// to make, so /routes/add goes straight to the VFR map. Rendered as a modal over the routes list.
function AddRouteChooserPage() {
  const navigate = useNavigate();
  const selectedOrg = useAppSelector(selectedOrganization);

  if (!selectedOrg?.ifrEnabled) {
    return <Navigate to="/routes/map" replace />;
  }

  return (
    <Modal show onHide={() => navigate("/routes")} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>New Route</Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-4">
        <div className="text-center text-muted mb-4">What kind of route is this?</div>

        <Row className="g-3">
          <Col xs={12} md={4}>
            <RouteKindCard
              code="VFR"
              title="Visual Route"
              points={["Draw the route on the map", "Waypoints and intermediate stops", "Filed to the route's AFTN addresses"]}
              onSelect={() => navigate("/routes/map")}
            />
          </Col>
          <Col xs={12} md={4}>
            <RouteKindCard
              code="IFR"
              title="IFR Route"
              points={["Airway route (Field 15) template", "Validated with EUROCONTROL (IFPUV)", "Re-checked whenever used on a flight"]}
              onSelect={() => navigate("/routes/add/ifr")}
            />
          </Col>
          <Col xs={12} md={4}>
            <RouteKindCard
              code="Y/Z"
              title="Mixed VFR / IFR"
              points={["Departs VFR, picks up IFR en route (Z) — or the reverse (Y)", "IFR portion EUROCONTROL-validated", "Re-checked whenever used on a flight"]}
              actions={[
                { title: "Depart VFR → IFR (Z)", onClick: () => navigate("/routes/add/ifr?rules=Z") },
                { title: "Depart IFR → VFR (Y)", onClick: () => navigate("/routes/add/ifr?rules=Y") },
              ]}
              onSelect={() => navigate("/routes/add/ifr?rules=Z")}
            />
          </Col>
        </Row>
      </Modal.Body>
    </Modal>
  );
}

interface RouteKindCardProps {
  code: string;
  title: string;
  points: string[];
  onSelect: () => void;
  buttonTitle?: string;
  //when set, the card renders these buttons instead of the single one (e.g. Mixed → Z / Y)
  actions?: { title: string; onClick: () => void }[];
}

function RouteKindCard({ code, title, points, onSelect, buttonTitle, actions }: RouteKindCardProps) {
  return (
    <div
      className="border rounded-3 p-4 h-100 d-flex flex-column"
      role={actions ? undefined : "button"}
      onClick={actions ? undefined : onSelect}
      style={{ cursor: "pointer", transition: "box-shadow 0.15s ease, transform 0.15s ease" }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = "0 6px 24px rgba(0,0,0,0.12)";
        e.currentTarget.style.transform = "translateY(-2px)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = "none";
        e.currentTarget.style.transform = "none";
      }}
    >
      <div className="d-flex align-items-center gap-3 mb-3">
        <span
          className="badge rounded-2 d-inline-flex align-items-center justify-content-center"
          style={{
            width: "64px",
            height: "44px",
            fontSize: "1.05rem",
            letterSpacing: "0.06em",
            //same colors as the rules pills in the flights list: VFR green, IFR red, mixed = both
            background:
              code === "IFR"
                ? "#dc3545"
                : code === "VFR"
                ? "#198754"
                : "linear-gradient(135deg, #198754 50%, #dc3545 50%)",
          }}
        >
          {code}
        </span>
        <span className="fw-semibold fs-5">{title}</span>
      </div>
      <ul className="text-muted small mb-4 ps-3" style={{ lineHeight: 1.9 }}>
        {points.map((p, i) => (
          <li key={i}>{p}</li>
        ))}
      </ul>
      <div className="d-grid gap-2 mt-auto">
        {actions ? (
          actions.map((a) => (
            <Button key={a.title} variant="dark" onClick={a.onClick}>
              {a.title}
            </Button>
          ))
        ) : (
          <Button variant="dark" onClick={onSelect}>
            {buttonTitle ?? `Create ${code} route`}
          </Button>
        )}
      </div>
    </div>
  );
}

export default AddRouteChooserPage;
